import React, { useState } from 'react'
import { matchPath } from 'react-router'
import { Formik, FormikProps } from 'formik'
import { useLocation } from 'react-use'
import { Button, ModalInjectedProps, Segment } from '@sketch/components'
import {
  GetProfilePublicationsDocument,
  useGetSharesForDocumentSelectionQuery,
  useUpdateWorkspaceProfileMutation,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { routes } from '@sketch/modules-common'
import { useToast } from '@sketch/toasts'
import {
  Modal,
  ModalBody,
  TabsContainer,
  TabSkeleton,
} from './ProfileModal.styles'
import { useProfileShortURL } from '../../hooks/useProfileShortURL'
import { initialValues, validationSchema } from './form'
import { useGetWorkspaceProfile } from '../../hooks'
import { UpdateProfileDetailsStep } from './UpdateProfileDetailsStep'
import { SelectDocumentsStep } from './SelectDocumentsStep'

interface UpdateProfileModalProps extends ModalInjectedProps {
  workspace: WorkspaceMinimalFragment
}

export const UpdateProfileModal = ({
  workspace,
  hideModal,
}: UpdateProfileModalProps) => {
  const { pathname } = useLocation()
  const isProfileEditRoute = !!matchPath(
    pathname ?? '',
    routes.WORKSPACE_PROFILE_EDIT.template()
  )

  const { showToast } = useToast()

  const profileResult = useGetWorkspaceProfile({
    variables: {
      workspaceIdentifier: workspace.identifier,
    },
  })
  const profileData = profileResult.profile
  const profileIdentifier = profileData?.identifier
  const shortUrlName = profileData?.shortUrlName || ''
  const description = profileData?.description || ''
  const websiteUrl = profileData?.websiteUrl || ''

  const [step, setStep] = useState(0)

  const { currentShortUrl } = useProfileShortURL(shortUrlName)

  const composedInitialFormValue = {
    ...initialValues,
    shortUrlName: currentShortUrl,
    description,
    websiteUrl,
  }

  const [updateWorkspaceProfile, { loading: updateWorkspaceProfileLoading }] =
    useUpdateWorkspaceProfileMutation({
      onError: 'show-toast',
      onCompleted: () => {
        showToast('Profile successfully updated')
        hideModal()
      },
    })

  const { data, loading: loadingShares } =
    useGetSharesForDocumentSelectionQuery({
      variables: {
        identifier: workspace.identifier,
        search: {
          filter: 'EXCLUDE_PERSONAL_DRAFTS',
        },
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    })

  const loading = loadingShares || updateWorkspaceProfileLoading

  const renderStep = (formikBag: FormikProps<any>) => {
    switch (step) {
      case 0: {
        return (
          <UpdateProfileDetailsStep
            workspace={workspace}
            formikBag={formikBag}
          />
        )
      }
      case 1: {
        return (
          <SelectDocumentsStep
            workspace={workspace}
            formikBag={formikBag}
            isProfileEditRoute={isProfileEditRoute}
          />
        )
      }
    }
  }

  const handleSubmitButtonAction = async (formikBag: FormikProps<any>) => {
    const { values } = formikBag
    const { selectAll, checkProfileURLLoading, ...formValues } = values
    // Given that shortUrlName validation is not part of formik, we need
    // to make sure the there's no current error with shortUrlName before
    // triggering validation
    if (formikBag.errors.shortUrlName) {
      return
    }

    const errors = await formikBag.validateForm()

    formikBag.setTouched(
      Object.keys(formValues).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {} as Record<string, boolean>
      )
    )

    if (Object.keys(errors).length === 0) {
      return updateWorkspaceProfile({
        variables: {
          input: { identifier: profileIdentifier, ...formValues },
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GetProfilePublicationsDocument,
            variables: { workspaceIdentifier: workspace.identifier },
          },
        ],
      })
    }
  }

  const sharesWithPublication = data?.workspace.shares?.entries
    ?.filter(({ publication }) => !!publication)
    .map(({ identifier }) => identifier)

  return (
    <Formik
      initialValues={{
        ...composedInitialFormValue,
        shares: sharesWithPublication,
      }}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      // eslint-disable-next-line no-console
      onSubmit={() => {}}
    >
      {formikBag => (
        <Modal onCancel={hideModal}>
          <ModalBody>
            <TabsContainer>
              <div onClick={() => setStep(0)}>
                <Segment
                  type="text"
                  label="Profile Details"
                  active={step === 0}
                />
              </div>
              <div onClick={() => setStep(1)}>
                {loadingShares ? (
                  <TabSkeleton />
                ) : (
                  <Segment
                    type="text"
                    label={`Published Documents (${sharesWithPublication?.length ?? 0})`}
                    active={step === 1}
                  />
                )}
              </div>
            </TabsContainer>
            {renderStep(formikBag)}
          </ModalBody>
          <Modal.Footer>
            <Button
              variant="secondary"
              disabled={loading || formikBag.values.checkProfileURLLoading}
              size="40"
              onClick={hideModal}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={loading || formikBag.values.checkProfileURLLoading}
              loading={loading}
              size="40"
              onClick={() => handleSubmitButtonAction(formikBag)}
            >
              Publish Changes
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  )
}
